import React, { useEffect, useMemo, useState } from "react";

import { Typography, LoadingIcon } from "@packages/ds";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import Button from "ds/Button";
import * as Dialog from "ds/Dialog";
import * as Sticker from "ds/Sticker";
import useDecodedParams from "Hooks/useDecodedParams";
import useQueryParams from "Hooks/useQueryStrings";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { servicesSelector } from "Reducers/environment/service/service.selectors";
import {
  setAutoscalingConfigurationThunk,
  getAutoscalingConfigurationThunk
} from "Reducers/environment/service/service.slice";
import { gitProjectSelector } from "Reducers/project/project";
import Logo from "src/common/containers/NavBar/Logo/Logo";
import { useAutoscallingValues } from "src/organization/pages/project/environment/metrics/containers/Service/util";
import { useAppSelector, useAppDispatch } from "Store/hooks";

import { useStubService } from "../metrics/resources/grid/util";

import * as S from "./ConfigureAutoscalingPage.style";

interface SelectOption {
  label: string;
  value: number;
  id: string;
}

const generateInstanceOptions = (min: number, max: number): SelectOption[] => {
  const options: SelectOption[] = [];
  for (let i = min; i <= max; i++) {
    options.push({
      label: i.toString(),
      value: i,
      id: i.toString()
    });
  }
  return options;
};

const identifyErrorTypes = (apiResponse, serviceName) => {
  if (!apiResponse?.errors || !Array.isArray(apiResponse.errors)) {
    return [];
  }

  const errorMappings = [
    {
      apiField: `services.${serviceName}.triggers.cpu.up.threshold`,
      formField: "upperThreshold"
    },
    {
      apiField: `services.${serviceName}.triggers.cpu.down.threshold`,
      formField: "lowerThreshold"
    },
    {
      apiField: `services.${serviceName}.instances.min`,
      formField: "minInstances"
    },
    {
      apiField: `services.${serviceName}.instances.max`,
      formField: "maxInstances"
    }
  ];

  return apiResponse.errors.map(error => {
    const match = errorMappings.find(
      mapping => mapping.apiField === error.field
    );

    return {
      type: match ? "KNOWN_ERROR" : "UNKNOWN_ERROR",
      message: error.message,
      field: error.field,
      affectedFields: match ? [match.formField] : [],
      original: error
    };
  });
};

const formatDurations = (duration: number): SelectOption => {
  return {
    label: `${duration / 60} ${duration / 60 > 1 ? "minutes" : "minute"}`,
    value: duration,
    id: duration.toString()
  };
};

const durationOptions = [
  { label: "1 minute", value: 60, id: "60" },
  { label: "2 minutes", value: 120, id: "120" },
  { label: "5 minutes", value: 300, id: "300" },
  { label: "10 minutes", value: 600, id: "600" },
  { label: "30 minutes", value: 1800, id: "1800" },
  { label: "60 minutes", value: 3600, id: "3600" }
];

const ConfigureAutoscalingPage = () => {
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const navigate = useNavigate();

  const [fieldErrors, setFieldErrors] = useState({
    upperThreshold: false,
    lowerThreshold: false,
    minInstances: false,
    maxInstances: false
  });

  const [errorMessages, setErrorMessages] = useState({
    genericMessage:
      "An error appeared while saving your changes. Please check the values and try again.",
    specificMessages: []
  });
  const { service } = useQueryParams(["service"]);
  const { all } = useStubService();

  const deploymentService = useMemo(
    () => all?.find(({ name }) => name === service),
    [all, service]
  );

  const serviceName = deploymentService?.name;
  const [isThresholdsEnabled, setEnabledThresholds] = useState<boolean>();
  const [minInstances, setMinInstances] = useState<SelectOption>({
    label: "1",
    value: 1,
    id: "1"
  });
  const [maxInstances, setMaxInstances] = useState<SelectOption>({
    label: "1",
    value: 1,
    id: "1"
  });
  const [selectedService, setSelectedService] = useState(serviceName);

  const { canManageAutoscaling } = useAutoscallingValues(serviceName);

  const [thresholdUpperDurationValue, setThresholdUpperDurationValue] =
    useState(durationOptions[1]);

  const [thresholdLowerDurationValue, setThresholdLowerDurationValue] =
    useState(durationOptions[1]);

  const [upperCoolDownDuration, setUpperCoolDownDuration] = useState(
    durationOptions[1]
  );
  const [lowerCoolDownDuration, setLowerCoolDownDuration] = useState(
    durationOptions[1]
  );

  const { projectId, environmentId } = useDecodedParams();

  const autoscalingConfig = useAppSelector(servicesSelector);
  const { services, defaults } =
    autoscalingConfig?.autoscalingConfiguration || {};
  const [thresholdUpperValue, setThresholdUpperValue] = useState<number>(80);
  const [thresholdLowerValue, setThresholdLowerValue] = useState<number>(20);
  const project = useSelectorWithUrlParams(gitProjectSelector);

  const handleApiError = error => {
    const newFieldErrors = {
      upperThreshold: false,
      lowerThreshold: false,
      minInstances: false,
      maxInstances: false
    };

    const errorInfos = identifyErrorTypes(error, selectedService);

    if (errorInfos.length) {
      errorInfos.forEach(err => {
        err.affectedFields.forEach(field => {
          newFieldErrors[field] = true;
        });
      });

      setFieldErrors(newFieldErrors);
      setErrorMessages({
        genericMessage: error.message,
        specificMessages: errorInfos.map(err => err.message)
      });
    } else {
      setErrorMessages({
        genericMessage: `${intl.formatMessage({
          id: "autoscaling.generic.error.message"
        })}`,
        specificMessages: []
      });
    }

    return "An unexpected error occurred";
  };

  useEffect(() => {
    dispatch(getAutoscalingConfigurationThunk({ projectId, environmentId }));
  }, [environmentId, projectId, dispatch, serviceName]);

  useEffect(() => {
    if (serviceName) {
      setSelectedService(serviceName);
    }
    if (
      !autoscalingConfig.autoscalingLoading &&
      autoscalingConfig.autoscalingConfiguration &&
      !autoscalingConfig.autoscalingError &&
      serviceName
    ) {
      const isEnabled = services?.[serviceName]?.enabled;
      const upperThresholdValue = services?.[serviceName]?.triggers?.cpu?.up
        ?.threshold
        ? services?.[serviceName]?.triggers?.cpu?.up?.threshold
        : defaults?.triggers?.cpu?.up?.threshold;
      const lowerThresholdValue = services?.[serviceName]?.triggers?.cpu?.down
        ?.threshold
        ? services?.[serviceName]?.triggers?.cpu?.down?.threshold
        : defaults?.triggers?.cpu.down?.threshold;
      const upperDuration = services?.[serviceName]?.triggers?.cpu?.up?.duration
        ? services?.[serviceName]?.triggers?.cpu?.up?.duration
        : defaults?.triggers?.cpu?.up?.duration;
      const lowerDuration = services?.[serviceName]?.triggers?.cpu?.down
        ?.duration
        ? services?.[serviceName]?.triggers?.cpu?.down?.duration
        : defaults?.triggers?.cpu?.down?.duration;

      const minInstancesValues = services?.[serviceName]?.instances?.min
        ? services?.[serviceName]?.instances?.min
        : defaults?.instances?.min;

      const minInstances = {
        label: minInstancesValues,
        value: minInstancesValues,
        id: minInstancesValues
      };

      const maxInstancesValues = services?.[serviceName]?.instances?.max
        ? services?.[serviceName]?.instances?.max
        : defaults?.instances?.max;

      const maxInstances = {
        label: maxInstancesValues,
        value: maxInstancesValues,
        id: maxInstancesValues
      };

      if (upperDuration) {
        const formattedUpperDuration = formatDurations(upperDuration);
        setThresholdUpperDurationValue(formattedUpperDuration);
      }
      if (lowerDuration) {
        const formattedLowerDuration = formatDurations(lowerDuration);

        setThresholdLowerDurationValue(formattedLowerDuration);
      }

      setMinInstances(minInstances);
      setMaxInstances(maxInstances);
      const lowerCoolDownDuration = services?.[serviceName]?.scale_cooldown
        ?.down
        ? services?.[serviceName]?.scale_cooldown?.down
        : defaults?.scale_cooldown?.down;
      const upperCoolDownDuration = services?.[serviceName]?.scale_cooldown?.up
        ? services?.[serviceName]?.scale_cooldown?.up
        : defaults?.scale_cooldown?.up;

      if (upperCoolDownDuration) {
        const formattedUpperCoolDownDuration = formatDurations(
          upperCoolDownDuration
        );
        setUpperCoolDownDuration(formattedUpperCoolDownDuration);
      }

      if (lowerCoolDownDuration) {
        const formattedLowerCoolDownDuration = formatDurations(
          lowerCoolDownDuration
        );
        setLowerCoolDownDuration(formattedLowerCoolDownDuration);
      }

      setThresholdUpperValue(upperThresholdValue);
      setThresholdLowerValue(lowerThresholdValue);

      if (isEnabled !== undefined) {
        setEnabledThresholds(isEnabled);
      }
    }
  }, [autoscalingConfig, serviceName]);

  const handleClosePage = () => {
    navigate(-1);
  };
  const handleConfigureThresholds = async () => {
    if (!selectedService) {
      return;
    }

    setFieldErrors({
      upperThreshold: false,
      lowerThreshold: false,
      minInstances: false,
      maxInstances: false
    });

    const settings = {
      services: {
        [selectedService]: {
          enabled: isThresholdsEnabled ?? false,
          triggers: {
            cpu: {
              up: {
                threshold: Number(
                  thresholdUpperValue !== null &&
                    thresholdUpperValue !== undefined
                    ? thresholdUpperValue
                    : 80
                ),
                duration: thresholdUpperDurationValue.value
              },
              down: {
                threshold: Number(
                  thresholdLowerValue !== null &&
                    thresholdLowerValue !== undefined
                    ? thresholdLowerValue
                    : 20
                ),
                duration: thresholdLowerDurationValue.value
              }
            }
          },
          scale_cooldown: {
            up: upperCoolDownDuration.value,
            down: lowerCoolDownDuration.value
          },
          instances: {
            min: Number(minInstances.value),
            max: Number(maxInstances.value)
          }
        }
      }
    };

    try {
      await dispatch(
        setAutoscalingConfigurationThunk({ projectId, environmentId, settings })
      ).unwrap();

      navigate(-1);
    } catch (error) {
      handleApiError(error);
    }
  };

  if (!all || all.length === 0) {
    return <LoadingIcon />;
  }

  return (
    <S.ConfigureAutoscalingWrapper>
      <S.VendorIconWrapper>
        <Button to="/">
          <Logo isFullLogo />
        </Button>
      </S.VendorIconWrapper>
      <S.DescriptionWrapper>
        <Typography.Label>{`${project?.title ?? ""} >  ${environmentId}`}</Typography.Label>

        <Typography.Title tag="h1">
          {intl.formatMessage({
            id: "configure.alarm.thresholds"
          })}
        </Typography.Title>
        <Typography.BodyText>
          {intl.formatMessage(
            {
              id: "define.thresholds.modal"
            },
            {
              br: () => <br />
            }
          )}
        </Typography.BodyText>

        {!!autoscalingConfig.autoscalingError && (
          <S.StickerError priority="critical" variant="short">
            <S.ErrorsContent>
              <S.GenericError>
                <Sticker.Icon priority="critical" />

                <Sticker.Text>
                  {errorMessages.genericMessage
                    ? errorMessages.genericMessage
                    : intl.formatMessage({
                        id: "autoscaling.generic.error.message"
                      })}
                </Sticker.Text>
              </S.GenericError>
              <S.ErrorsList>
                {errorMessages.specificMessages.length > 0 &&
                  errorMessages.specificMessages.map(msg => (
                    <Typography.BodyText key={msg}>{msg}</Typography.BodyText>
                  ))}
              </S.ErrorsList>
            </S.ErrorsContent>
          </S.StickerError>
        )}

        {!canManageAutoscaling && (
          <S.StickerPermissions priority="information">
            <Sticker.Icon priority="information" />
            <Sticker.Text>
              {intl.formatMessage({
                id: "autoscaling.permission.request"
              })}
            </Sticker.Text>
          </S.StickerPermissions>
        )}
      </S.DescriptionWrapper>

      {autoscalingConfig.autoscalingLoading ? (
        <S.LoadingWrapper>
          <LoadingIcon />
        </S.LoadingWrapper>
      ) : (
        <S.ContentWrapper>
          <S.AppsAndResources>
            <S.SelectedAppContainer>
              <S.ApplicationLabel size="extraSmall" weight="semiBold">
                Application
              </S.ApplicationLabel>
              <Typography.BodyText weight="bold">
                {selectedService}
              </Typography.BodyText>
            </S.SelectedAppContainer>
          </S.AppsAndResources>
          <hr />
          <Typography.BodyText weight="semiBold">
            {intl.formatMessage({
              id: "autoscaling.triggers.title"
            })}
          </Typography.BodyText>

          <S.EnableThresholds>
            <S.EnableCheckbox
              checked={!!isThresholdsEnabled}
              onCheckedChanged={() => {
                setEnabledThresholds(!isThresholdsEnabled);
              }}
              label="Enable CPU triggers"
              forId="enable-thresholds"
            />
          </S.EnableThresholds>
          <S.ThresholdsValues>
            <S.UpperThreshold>
              {fieldErrors.upperThreshold && <S.ErrorDot />}

              <Typography.Label>
                {intl.formatMessage(
                  {
                    id: "add.n.instances"
                  },
                  {
                    strong: (content: React.ReactNode) => (
                      <S.BoldText weight="semiBold">{content}</S.BoldText>
                    ),
                    metric: "CPU"
                  }
                )}
              </Typography.Label>

              <Typography.BodyText>
                {intl.formatMessage(
                  {
                    id: "add.n.instances.when.up"
                  },
                  {
                    strong: (content: React.ReactNode) => (
                      <S.BoldText weight="semiBold">{content}</S.BoldText>
                    ),
                    metric: "CPU"
                  }
                )}
              </Typography.BodyText>
              <S.ValuesWrapper>
                <S.ThresholdInput
                  id="upper-threshold"
                  placeholder="80"
                  value={thresholdUpperValue}
                  onChange={e => {
                    setThresholdUpperValue(e.target.value as unknown as number);
                  }}
                  isValid={!fieldErrors.upperThreshold}
                />
                <Typography.BodyText>
                  {intl.formatMessage({
                    id: "duration.of"
                  })}
                </Typography.BodyText>
                <S.DurationOptions
                  options={durationOptions}
                  defaultValue={durationOptions[1]}
                  value={thresholdUpperDurationValue}
                  onChange={value => {
                    setThresholdUpperDurationValue(value as SelectOption);
                  }}
                />
              </S.ValuesWrapper>
            </S.UpperThreshold>
            <S.LowerThreshold>
              {fieldErrors.lowerThreshold && <S.ErrorDot />}
              <Typography.Label>
                {intl.formatMessage(
                  {
                    id: "remove.n.instances"
                  },
                  {
                    strong: (content: React.ReactNode) => (
                      <S.BoldText weight="semiBold">{content}</S.BoldText>
                    ),
                    metric: "CPU"
                  }
                )}
              </Typography.Label>

              <Typography.BodyText>
                {intl.formatMessage(
                  {
                    id: "add.n.instances.when.down"
                  },
                  {
                    strong: (content: React.ReactNode) => (
                      <S.BoldText weight="semiBold">{content}</S.BoldText>
                    ),
                    metric: "CPU"
                  }
                )}
              </Typography.BodyText>
              <S.ValuesWrapper>
                <S.ThresholdInput
                  placeholder="20"
                  value={thresholdLowerValue}
                  onChange={e => {
                    setThresholdLowerValue(e.target.value as unknown as number);
                  }}
                  isValid={!fieldErrors.lowerThreshold}
                />
                <Typography.BodyText>
                  {intl.formatMessage({
                    id: "duration.of"
                  })}
                </Typography.BodyText>
                <S.DurationOptions
                  options={durationOptions}
                  value={thresholdLowerDurationValue}
                  defaultValue={durationOptions[1]}
                  onChange={value => {
                    setThresholdLowerDurationValue(value as SelectOption);
                  }}
                />
              </S.ValuesWrapper>
            </S.LowerThreshold>
          </S.ThresholdsValues>
          <hr />

          <S.CoolDownWrapper>
            <Typography.BodyText weight="semiBold">
              {intl.formatMessage({
                id: "cooldown.section.title"
              })}
            </Typography.BodyText>

            <S.UpperCoolDown>
              <S.ScalingUp>
                {intl.formatMessage({
                  id: "scaling.up"
                })}
              </S.ScalingUp>
              <S.UpperCoolDownDetails>
                <Typography.BodyText>
                  {intl.formatMessage({
                    id: "then.wait"
                  })}
                </Typography.BodyText>

                <S.CoolDownDurationOptions
                  options={durationOptions}
                  defaultValue={durationOptions[1]}
                  value={upperCoolDownDuration}
                  onChange={value => {
                    setUpperCoolDownDuration(value as SelectOption);
                  }}
                />
                <Typography.BodyText>
                  {intl.formatMessage({
                    id: "before.adding.more"
                  })}
                </Typography.BodyText>
              </S.UpperCoolDownDetails>
            </S.UpperCoolDown>

            <S.LowerCoolDown>
              <S.ScalingDown>
                {intl.formatMessage({
                  id: "scaling.down"
                })}
              </S.ScalingDown>
              <S.LowerCoolDownDetails>
                <Typography.BodyText>
                  {intl.formatMessage({
                    id: "then.wait"
                  })}
                </Typography.BodyText>

                <S.CoolDownDurationOptions
                  options={durationOptions}
                  defaultValue={durationOptions[1]}
                  value={lowerCoolDownDuration}
                  onChange={value => {
                    setLowerCoolDownDuration(value as SelectOption);
                  }}
                />
                <Typography.BodyText>
                  {intl.formatMessage({
                    id: "before.removing.more"
                  })}
                </Typography.BodyText>
              </S.LowerCoolDownDetails>
            </S.LowerCoolDown>
          </S.CoolDownWrapper>

          <hr />

          <S.InstancesWrapper>
            <Typography.BodyText weight="semiBold">
              {intl.formatMessage({
                id: "instances.section.title"
              })}
            </Typography.BodyText>
            <S.MinimumInstancesWrapper>
              <S.LabelWrapper>
                <S.InstancesLabel isValid={!fieldErrors.minInstances}>
                  {" "}
                  {intl.formatMessage({
                    id: "min.instances.dialog.title"
                  })}
                </S.InstancesLabel>
                <S.MinInstancesDialog>
                  <Dialog.Trigger>
                    <S.InfoIcon color="var(--mode-vector-neutral-default)" />
                  </Dialog.Trigger>

                  <S.MinContent>
                    <Typography.BodyText size="small" weight="semiBold">
                      {intl.formatMessage({
                        id: "min.instances.dialog.title"
                      })}
                    </Typography.BodyText>
                    <Typography.BodyText>
                      {intl.formatMessage({
                        id: "min.instances.dialog.description"
                      })}
                    </Typography.BodyText>
                  </S.MinContent>
                </S.MinInstancesDialog>
              </S.LabelWrapper>
              <S.InstancesDetails>
                <Typography.BodyText>
                  {intl.formatMessage({
                    id: "when.instances"
                  })}
                </Typography.BodyText>
                <S.MinimumInstances
                  id="min-instances"
                  value={minInstances}
                  options={generateInstanceOptions(
                    defaults?.instances?.min || 1,
                    defaults?.instances?.max || 1
                  )}
                  onChange={value => {
                    setMinInstances(value as SelectOption);
                  }}
                />
                <Typography.BodyText>
                  {intl.formatMessage({
                    id: "min.instances.text"
                  })}
                </Typography.BodyText>
              </S.InstancesDetails>
            </S.MinimumInstancesWrapper>
            <S.MaximumInstancesWrapper>
              <S.LabelWrapper>
                <S.InstancesLabel isValid={!fieldErrors.maxInstances}>
                  {" "}
                  {intl.formatMessage({
                    id: "max.instances.dialog.title"
                  })}
                </S.InstancesLabel>
                <S.MaxInstancesDialog>
                  <Dialog.Trigger>
                    <S.InfoIcon color="var(--mode-vector-neutral-default)" />
                  </Dialog.Trigger>
                  <S.MaxContent>
                    <Typography.BodyText size="small" weight="semiBold">
                      {intl.formatMessage({
                        id: "max.instances.dialog.title"
                      })}
                    </Typography.BodyText>
                    <Typography.BodyText>
                      {intl.formatMessage({
                        id: "max.instances.dialog.description"
                      })}
                    </Typography.BodyText>
                  </S.MaxContent>
                </S.MaxInstancesDialog>
              </S.LabelWrapper>
              <S.InstancesDetails>
                <Typography.BodyText>
                  {intl.formatMessage({
                    id: "when.instances"
                  })}
                </Typography.BodyText>
                <S.MaximumInstances
                  id="max-instances"
                  onChange={value => {
                    setMaxInstances(value as SelectOption);
                  }}
                  value={maxInstances}
                  options={generateInstanceOptions(
                    defaults?.instances?.min || 1,
                    defaults?.instances?.max || 1
                  )}
                />
                <Typography.BodyText>
                  {intl.formatMessage({
                    id: "max.instances.text"
                  })}
                </Typography.BodyText>
              </S.InstancesDetails>
            </S.MaximumInstancesWrapper>
          </S.InstancesWrapper>

          <S.ActionButtons>
            <Button
              variant="secondary"
              onClick={() => {
                handleClosePage();
              }}
            >
              Cancel
            </Button>
            <Button onClick={() => handleConfigureThresholds()}>Save</Button>
          </S.ActionButtons>
        </S.ContentWrapper>
      )}
    </S.ConfigureAutoscalingWrapper>
  );
};

export default ConfigureAutoscalingPage;
